import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Loader } from '@common/components';
import { USMapChoropleth } from '@common/components/builder/organisms/Maps';
import { PopupCountyInfoValue } from '@common/components/builder/organisms/Maps/PopupCountyInfo/PopupCountyInfo.types';
import { COMMON_TRANSLATION, DEFAULT_HEIGHT, DEFAULT_WIDTH } from '@common/constants';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { useLayoutThreeStore } from '@modules/study-insights/store';

import { PillIds } from '../../../shared/types/PillIds';
import { StyledError, StyledErrorContainer } from './ParticipantDensityMap.styles';
import { ParticipantDensityMapProps } from './ParticipantDensityMap.types';
import { HighlightedFeature } from '@/common/components/builder/organisms/Maps/MapChoropleth/MapChoropleth.types';

const ParticipantDensityMap: FC<ParticipantDensityMapProps> = ({
  height = DEFAULT_HEIGHT,
  tooltipText,
  width = DEFAULT_WIDTH,
}) => {
  const { chartMode, mapData: data, isError, isSuccess, isLoading } = useLayoutThreeStore();
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);

  const buildPopupInfo = useCallback(
    (fetchHhcParticipantsByZctas: HighlightedFeature): PopupCountyInfoValue[] => {
      let value: number | string = fetchHhcParticipantsByZctas.value;
      if (chartMode === PillIds.RATE_CONSENTED || chartMode === PillIds.RATE_INTERESTED) {
        value = `${value}%`;
      }
      const info = [
        {
          id: 'popup-county-info-name',
          label: t('participant_recruitment.popup.zip_code'),
          value: fetchHhcParticipantsByZctas.id,
        },
        {
          id: 'popup-county-info-value',
          label: tooltipText,
          value,
        },
      ];

      return info;
    },
    [data, chartMode, tooltipText],
  );

  return (
    <Box data-testid="density-map" css={{ height, width }}>
      {isLoading && <Loader css={{ width, height }} />}
      {!isLoading && isSuccess && (
        <Box data-testid="participant-density-map">
          <USMapChoropleth width={width} height={height} zctas={data} buildPopupInfo={buildPopupInfo} />
        </Box>
      )}
      {!isLoading && isError && (
        <StyledErrorContainer css={{ height }} data-testid="participant-density-map-error">
          <StyledError>{t('errors.generic', { ns: COMMON_TRANSLATION })}</StyledError>
        </StyledErrorContainer>
      )}
    </Box>
  );
};

export default ParticipantDensityMap;
